// Centralized initialization manager for robust app startup
import { initializeLocale } from './locale';
import { checkSupabaseHealth, clearHealthCache } from './supabase-health';

// Feature detection
const detectFeatures = (): Record<string, boolean> => {
  return {
    localStorage: typeof localStorage !== 'undefined',
    sessionStorage: typeof sessionStorage !== 'undefined',
    indexedDB: typeof indexedDB !== 'undefined',
    webGL: (() => {
      try {
        const canvas = document.createElement('canvas');
        return !!(canvas.getContext('webgl') || canvas.getContext('experimental-webgl'));
      } catch {
        return false;
      }
    })(),
    intersectionObserver: typeof IntersectionObserver !== 'undefined',
    performanceAPI: typeof performance !== 'undefined' && typeof performance.mark !== 'undefined'
  };
};

// Analytics de conectividade
class ConnectivityAnalytics {
  private metrics: Array<{
    type: string;
    timestamp: number;
    success: boolean;
    responseTime?: number;
    error?: string;
  }> = [];

  logMetric(type: string, success: boolean, responseTime?: number, error?: string) {
    this.metrics.push({
      type,
      timestamp: Date.now(),
      success,
      responseTime,
      error
    });
    
    // Manter apenas os últimos 100 registros
    if (this.metrics.length > 100) {
      this.metrics = this.metrics.slice(-50);
    }
  }

  getSuccessRate(type?: string, timeWindow = 3600000): number { // 1 hora por padrão
    const cutoff = Date.now() - timeWindow;
    const relevantMetrics = this.metrics.filter(m => 
      m.timestamp > cutoff && (!type || m.type === type)
    );
    
    if (relevantMetrics.length === 0) return 100;
    
    const successCount = relevantMetrics.filter(m => m.success).length;
    return Math.round((successCount / relevantMetrics.length) * 100);
  }

  getAverageResponseTime(type?: string, timeWindow = 3600000): number {
    const cutoff = Date.now() - timeWindow;
    const relevantMetrics = this.metrics.filter(m => 
      m.timestamp > cutoff && 
      m.responseTime !== undefined && 
      (!type || m.type === type)
    );
    
    if (relevantMetrics.length === 0) return 0;
    
    const totalTime = relevantMetrics.reduce((sum, m) => sum + (m.responseTime || 0), 0);
    return Math.round(totalTime / relevantMetrics.length);
  }

  getSummary() {
    return {
      healthCheckSuccessRate: this.getSuccessRate('health_check'),
      initializationSuccessRate: this.getSuccessRate('initialization'),
      averageHealthCheckTime: this.getAverageResponseTime('health_check'),
      totalMetrics: this.metrics.length,
      lastFailures: this.metrics.filter(m => !m.success).slice(-5)
    };
  }
}

const connectivityAnalytics = new ConnectivityAnalytics();

interface InitializationStep {
  name: string;
  fn: () => Promise<boolean> | boolean;
  critical: boolean;
  timeout?: number;
}

class InitManager {
  private steps: InitializationStep[] = [];
  private initialized = false;
  private initPromise: Promise<boolean> | null = null;

  constructor() {
    this.registerSteps();
  }

  private registerSteps() {
    // Step 1: Locale configuration (critical)
    this.steps.push({
      name: 'locale',
      fn: () => {
        initializeLocale();
        return true;
      },
      critical: true
    });

    // Step 2: Supabase health check (non-critical, timeout reduzido)
    this.steps.push({
      name: 'supabase',
      fn: checkSupabaseHealth,
      critical: false,
      timeout: 2000 // Reduced from 3000ms for even faster startup
    });

    // Step 3: Feature detection (critical)
    this.steps.push({
      name: 'features',
      fn: () => {
        // Check critical browser features
        const requiredFeatures = ['localStorage', 'fetch', 'Promise'];
        const missing = requiredFeatures.filter(feature => !(feature in window));
        
        if (missing.length > 0) {
          console.error('❌ Missing critical features:', missing);
          return false;
        }
        
        return true;
      },
      critical: true
    });
  }

  private async runStep(step: InitializationStep): Promise<boolean> {
    const stepStartTime = performance.now();

    
    try {
      const timeoutMs = step.timeout || 3000;
      
      const result = await Promise.race([
        Promise.resolve(step.fn()),
        new Promise<boolean>((_, reject) => 
          setTimeout(() => reject(new Error(`Timeout after ${timeoutMs}ms`)), timeoutMs)
        )
      ]);
      
      const stepTime = Math.round(performance.now() - stepStartTime);
      
      // Log métricas de sucesso
      connectivityAnalytics.logMetric(step.name.toLowerCase().replace(' ', '_'), true, stepTime);
      
      return result;
      
    } catch (error) {
      const stepTime = Math.round(performance.now() - stepStartTime);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      console.error(`❌ Step failed: ${step.name} (${stepTime}ms)`, error);
      
      // Log métricas de falha
      connectivityAnalytics.logMetric(step.name.toLowerCase().replace(' ', '_'), false, stepTime, errorMessage);
      
      if (step.critical) {
        throw new Error(`Critical step failed: ${step.name} - ${errorMessage}`);
      }
      
      return false;
    }
  }

  public async initialize(): Promise<boolean> {
    if (this.initialized) {
      return true;
    }
    
    if (this.initPromise) {
      return this.initPromise;
    }

    this.initPromise = this.doInitialize();
    return this.initPromise;
  }

  private async doInitialize(): Promise<boolean> {

    const startTime = Date.now();
    
    try {
      for (const step of this.steps) {
        const success = await this.runStep(step);
        
        if (!success && step.critical) {
          throw new Error(`Critical initialization step '${step.name}' failed`);
        }
      }
      
      const totalDuration = Date.now() - startTime;
      
      this.initialized = true;
      return true;
      
    } catch (error) {
      const totalDuration = Date.now() - startTime;
      console.error(`❌ Application initialization failed after ${totalDuration}ms:`, error);
      
      // Try recovery

      await this.attemptRecovery();
      
      return false;
    }
  }

  private async attemptRecovery(): Promise<void> {

    
    try {
      // Clear potentially corrupted cache items
      const itemsToDelete = [
        'supabase.auth.token',
        'supabase.session',
        'app.cache',
        'locale.cache',
        'user.preferences'
      ];
      
      itemsToDelete.forEach(item => {
        try {
          localStorage.removeItem(item);
          sessionStorage.removeItem(item);
        } catch (e) {
          // Silent fail for storage operations
        }
      });
      
      // Limpar cache de health check
      clearHealthCache();
      
      // Optional: trigger garbage collection if available
      if ('gc' in window && typeof (window as any).gc === 'function') {
        try {
          (window as any).gc();
        } catch (e) {
          // Silent fail for garbage collection
        }
      }
      
    } catch (error) {
      console.error('❌ Recovery attempt failed:', error);
    }
  }

  // Método público para obter métricas de conectividade
  public getConnectivityMetrics() {
    return connectivityAnalytics.getSummary();
  }

  // Método para forçar health check
  public async forceHealthCheck(): Promise<boolean> {
    const startTime = performance.now();
    try {
      const result = await checkSupabaseHealth(true); // Skip cache
      const responseTime = Math.round(performance.now() - startTime);
      connectivityAnalytics.logMetric('forced_health_check', result, responseTime);
      return result;
    } catch (error) {
      const responseTime = Math.round(performance.now() - startTime);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      connectivityAnalytics.logMetric('forced_health_check', false, responseTime, errorMessage);
      return false;
    }
  }

  public isInitialized(): boolean {
    return this.initialized;
  }

  public reset(): void {
    this.initialized = false;
    this.initPromise = null;
  }
}

export const initManager = new InitManager();

// Convenience function for React components
export const useInitialization = () => {
  return {
    initialize: () => initManager.initialize(),
    isInitialized: () => initManager.isInitialized(),
    reset: () => initManager.reset()
  };
};